import { cn } from "~/lib/utils";

import type { CSSProperties } from "react";

/**
 * Washi tape strip. Every variant is UNIQUE on purpose — different pattern, color,
 * width, torn-edge shape and tilt — because two identical "pieces of tape" is what
 * breaks the illusion. Pick a distinct `variant` at each call site (or pass `i` in a
 * loop); the look is deterministic per variant, so SSR stays stable.
 *
 * Positioning: absolute, centered above its parent's top edge by default. Override
 * placement with `className` (e.g. `!left-[14%]`, `!right-[10%]`) and tilt with `rotate`.
 */
type TapeVariant = {
  bg: string;
  clip: string;
  w: number;
  h: number;
  rotate: number;
  opacity: number;
};

const TAPES: TapeVariant[] = [
  {
    // teal, rayas diagonales
    bg: "repeating-linear-gradient(48deg, rgba(43,125,135,.42) 0 7px, rgba(43,125,135,.26) 7px 15px)",
    clip: "polygon(1% 6%, 99% 0, 98% 92%, 2% 100%)",
    w: 96,
    h: 26,
    rotate: -3.2,
    opacity: 0.74,
  },
  {
    // ámbar, rayas finas horizontales
    bg: "repeating-linear-gradient(90deg, rgba(164,98,34,.38) 0 5px, rgba(164,98,34,.22) 5px 11px)",
    clip: "polygon(2% 0, 100% 5%, 97% 100%, 0 94%)",
    w: 108,
    h: 23,
    rotate: 4.5,
    opacity: 0.7,
  },
  {
    // kraft, sólido translúcido con borde más rasgado
    bg: "linear-gradient(180deg, rgba(201,162,107,.5), rgba(201,162,107,.34))",
    clip: "polygon(3% 4%, 96% 0, 100% 46%, 97% 96%, 4% 100%, 0 52%)",
    w: 88,
    h: 30,
    rotate: -6.5,
    opacity: 0.62,
  },
  {
    // teal gingham (dos gradientes cruzados)
    bg: "repeating-linear-gradient(90deg, rgba(43,125,135,.30) 0 6px, transparent 6px 12px), repeating-linear-gradient(0deg, rgba(43,125,135,.30) 0 6px, transparent 6px 12px)",
    clip: "polygon(0 8%, 98% 2%, 100% 94%, 2% 98%)",
    w: 92,
    h: 27,
    rotate: 2.4,
    opacity: 0.66,
  },
  {
    // coral, diagonal inversa
    bg: "repeating-linear-gradient(-42deg, rgba(226,96,79,.36) 0 6px, rgba(226,96,79,.2) 6px 13px)",
    clip: "polygon(2% 2%, 100% 6%, 98% 98%, 0 92%)",
    w: 102,
    h: 22,
    rotate: -8.5,
    opacity: 0.7,
  },
  {
    // verde bosque, sólido con veta suave
    bg: "linear-gradient(90deg, rgba(53,122,80,.5), rgba(53,122,80,.32) 60%, rgba(53,122,80,.46))",
    clip: "polygon(1% 0, 97% 4%, 100% 100%, 3% 95%)",
    w: 96,
    h: 28,
    rotate: 5.5,
    opacity: 0.6,
  },
  {
    // postit amarillo con puntitos
    bg: "radial-gradient(rgba(120,90,20,.22) 1px, transparent 1.4px) 0 0/9px 9px, linear-gradient(180deg, rgba(246,227,174,.85), rgba(240,214,140,.7))",
    clip: "polygon(3% 3%, 98% 0, 96% 97%, 0 100%)",
    w: 110,
    h: 24,
    rotate: -2.2,
    opacity: 0.78,
  },
  {
    // teal, rayas anchas verticales
    bg: "repeating-linear-gradient(0deg, rgba(43,125,135,.4) 0 9px, rgba(43,125,135,.24) 9px 18px)",
    clip: "polygon(0 4%, 99% 0, 100% 96%, 1% 100%)",
    w: 84,
    h: 28,
    rotate: 7.5,
    opacity: 0.7,
  },
];

export function WashiTape({
  variant = 0,
  rotate,
  className,
  style,
}: {
  variant?: number;
  rotate?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const tape = TAPES[((variant % TAPES.length) + TAPES.length) % TAPES.length];
  const deg = rotate ?? tape.rotate;
  return (
    <span
      aria-hidden="true"
      className={cn("pointer-events-none absolute left-1/2 top-[-13px] z-10", className)}
      style={{
        width: tape.w,
        height: tape.h,
        opacity: tape.opacity,
        background: tape.bg,
        clipPath: tape.clip,
        transform: `translateX(-50%) rotate(${deg}deg)`,
        boxShadow: "0 1px 2px rgba(31,26,21,.14)",
        ...style,
      }}
    />
  );
}
